import { useEffect, useState } from 'react';
import type { RxDocument } from 'rxdb';
import { useRxCollection } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type {
  DeficiencyFiles,
  DeficiencyFilesCollection,
  DeficiencyFilesDocument,
} from './deficiencyFiles.types';

export const useDeficiencyFiles = () => {
  const { SiteDiscipline } = useMst();
  const deficiencyFilesCollection: DeficiencyFilesCollection = useRxCollection(
    RxdbCollectionName.DEFICIENCY_FILES,
  );
  const [deficiencyFilesInitialized, setDeficiencyFilesInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (deficiencyFilesCollection) {
      setDeficiencyFilesInitialized(true);
    }
  }, [deficiencyFilesCollection]);

  const insertFileIntoDeficiencyFile = async (fileObj: DeficiencyFiles) => {
    try {
      if (fileObj.deficiencyUUID !== '') await deficiencyFilesCollection?.upsert(fileObj);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useDeficiencyFiles.ts ~ line 27 ~ insertFileIntoDeficiencyFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFiles.tsx ~ line 38 ~ insertFileIntoDeficiencyFile ~',
      );
    }
  };

  /**
   * Subscribe to newly inserted attachments
   *
   * @param {(item: RxChangeEventInsert<DeficiencyFiles>) => void} next
   * @return {*}
   */
  const subscribeToDefAttachments = (
    handleAttachments: (attachments: DeficiencyFiles[]) => void,
  ) => {
    if (!deficiencyFilesCollection) return;
    const query = deficiencyFilesCollection.find();
    query.$.subscribe(handleAttachments);
  };

  const updateDeficiencyFile = async (
    fileUUId: string,
    name: string,
    showOnReport: boolean,
    fileDescription: string,
    isDelete: string,
    isReInspectedFile: boolean,
    fileSortOrder?: number,
  ): Promise<RxDocument<DeficiencyFiles> | null> => {
    if (!deficiencyFilesCollection) return null;

    try {
      if (deficiencyFilesCollection) {
        const updated = await deficiencyFilesCollection
          .findOne({
            selector: {
              fileAimsUUID: fileUUId,
            },
          })
          .update({
            $set: {
              fileName: name,
              showOnReport,
              fileDescription,
              isDelete,
              fileSortOrder,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
              isReInspectedFile,
            },
          });
        return updated;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFile.tsx ~ line 118 ~ updateDeficiencyFile ~',
      );
      throw error;
    }
    return null;
  };

  const deleteDeficiencyFile = async (
    fileUUId: string,
    isDelete: string,
  ): Promise<RxDocument<DeficiencyFiles> | null> => {
    if (!deficiencyFilesCollection) return null;

    try {
      if (deficiencyFilesCollection) {
        const updated = await deficiencyFilesCollection
          .findOne({
            selector: {
              fileAimsUUID: fileUUId,
            },
          })
          .update({
            $set: {
              isDelete,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
            },
          });
        return updated;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFile.tsx ~ line 155 ~ deleteDeficiencyFile ~',
      );
      throw error;
    }
    return null;
  };

  const getAllDeficiencyFiles = async (): Promise<DeficiencyFilesDocument[]> => {
    if (!deficiencyFilesCollection) return [];
    let deficiencyFiles: DeficiencyFilesDocument[];
    try {
      const site = SiteDiscipline.siteId;
      const discipline = SiteDiscipline.disciplineId;
      const queryObj = {
        selector: {
          $and: [{ siteId: site }, { disciplineId: discipline }, { isDelete: 'False' }],
        },
      };

      deficiencyFiles = await deficiencyFilesCollection?.find(queryObj).exec();
      return deficiencyFiles;
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyFiles.tsx ~ line 185 ~ getAllDeficiencyFiles ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  return {
    deficiencyFilesInitialized,
    deficiencyFilesCollection,
    insertFileIntoDeficiencyFile,
    subscribeToDefAttachments,
    updateDeficiencyFile,
    deleteDeficiencyFile,
    getAllDeficiencyFiles,
  };
};

export default useDeficiencyFiles;
